/**
 * `/admin/studies/new` — LATO staff create a study for an explicitly chosen organization
 * (`?org=` pre-fills it from the admin list's filter; the server validates the selector).
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
// zod/mini: the same parser, tree-shaken to what these search params use. Full zod would ride in
// the app's entry chunk, since validateSearch is not split out with the page.
import { z } from "zod/mini";
import { NewStudyPage } from "@/features/studies/NewStudyPage";
import { NewStudyOrganizationDialog } from "@/features/studies/NewStudyOrganizationDialog";
import { SCOPE_TABS } from "@/features/studies/scopeTabs";
import { parseStudiesListSearch, studiesListReturnSearch } from "@/features/studies/studiesList";
import { StudySurfaceProvider } from "@/features/studies/studySurface";

function AdminNewStudy() {
  const { brief, step, org } = Route.useSearch();
  const navigate = useNavigate();
  if (!org) {
    return (
      <NewStudyOrganizationDialog
        defaultOpen
        onContinue={(selectedOrg) => {
          void navigate({
            to: "/admin/studies/new",
            search: { ...(brief ? { brief } : {}), ...(step ? { step } : {}), org: selectedOrg },
            replace: true,
          });
        }}
        onCancel={() => {
          void navigate({
            to: "/admin/studies",
            search: studiesListReturnSearch("admin"),
            replace: true,
          });
        }}
      />
    );
  }
  return (
    <StudySurfaceProvider value="admin">
      <NewStudyPage key={org} brief={brief} step={step} org={org} />
    </StudySurfaceProvider>
  );
}

const base = z.object({
  brief: z.optional(z.string()),
  step: z.optional(z.enum(SCOPE_TABS)),
});

export const Route = createFileRoute("/_auth/admin/studies/new")({
  validateSearch: (search: Record<string, unknown>) => {
    const org = parseStudiesListSearch(search).org;
    return { ...base.parse(search), ...(org ? { org } : {}) };
  },
  component: AdminNewStudy,
});
